// src/appRWAPromo/utils/scanMultiChainAvail.js
import { auth } from "../../components/firebase"; // <-- проверь путь
import {
  OX_NATIVE,
  SUPPORTED_CHAINS,
  STABLE_DECIMALS_BY_CHAIN,
  USDC_DECIMALS_BY_CHAIN,
  TOKENS_BY_CHAIN,
} from "../components/Portfolio/tokens"; // <-- проверь путь

import { erc20Abi, formatUnits, createPublicClient, http } from "viem";
import { mainnet, bsc, polygon, arbitrum } from "viem/chains";

/* =========================
   CONFIG
========================= */

const USD_THRESHOLD = 5;

// mainnet (infura) более чувствителен к burst
const ERC20_CONCURRENCY_MAINNET = 2;
const ERC20_CONCURRENCY_DEFAULT = 4;

// native price cache
const NATIVE_USD_TTL = 60_000; // 60s, можно 30_000

// scan-level cache
const SCAN_TTL_MS = 30_000;

// retry for CF 429
const CF_429_MAX_RETRIES = 3;

/* =========================
   Helpers
========================= */

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function getCfBase() {
  return (process.env.REACT_APP_CF_BASE || "").replace(/\/+$/, "");
}

function is429Err(e) {
  const msg = String(e?.message || "");
  return msg.includes("429") || msg.toLowerCase().includes("too many requests");
}

function parseMaybeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * ✅ postAuthedJson: backoff on 429 + нормальная ошибка
 */
async function postAuthedJson(path, body, attempt = 0) {
  const base = getCfBase();
  if (!base) throw new Error("REACT_APP_CF_BASE is not set");

  const url = `${base}/${path}`;
  const token = await auth.currentUser?.getIdToken?.();
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });

  // ✅ 429 backoff (Retry-After если есть)
  if (res.status === 429 && attempt < CF_429_MAX_RETRIES) {
    const ra = res.headers.get("retry-after");
    const delayMs = ra
      ? Math.min(5000, Number(ra) * 1000)
      : Math.min(5000, 400 * Math.pow(2, attempt)); // 400,800,1600
    await sleep(delayMs);
    return postAuthedJson(path, body, attempt + 1);
  }

  const text = await res.text();
  const parsed = parseMaybeJson(text);

  if (!res.ok) {
    const msg =
      parsed?.userMessage ||
      parsed?.error ||
      parsed?.message ||
      text ||
      `Request failed (${res.status})`;
    throw new Error(String(msg));
  }

  return parsed ?? text;
}

/**
 * ✅ простой limiter без зависимостей
 */
async function mapLimit(items, limit, worker) {
  const ret = new Array(items.length);
  let idx = 0;

  const runners = Array.from({ length: Math.max(1, limit) }, async () => {
    while (idx < items.length) {
      const cur = idx++;
      ret[cur] = await worker(items[cur], cur);
    }
  });

  await Promise.all(runners);
  return ret;
}

/* =========================
   Viem Public Clients
========================= */

const CHAIN_VIEM = {
  1: mainnet,
  56: bsc,
  137: polygon,
  42161: arbitrum,
};

function rpcUrlForChain(chainId) {
  if (chainId === 1)
    return process.env.REACT_APP_RPC_MAINNET || process.env.REACT_APP_RPC_INFURA_FRONT || "";
  if (chainId === 56) return process.env.REACT_APP_RPC_BSC || "";
  if (chainId === 137) return process.env.REACT_APP_RPC_POLYGON || "";
  if (chainId === 42161) return process.env.REACT_APP_RPC_ARBITRUM || "";
  return "";
}

const pcCache = new Map(); // chainId -> publicClient
function makePublicClient(chainId) {
  const cid = Number(chainId);
  if (pcCache.has(cid)) return pcCache.get(cid);

  const chain = CHAIN_VIEM[cid];
  if (!chain) return null;

  const url = rpcUrlForChain(cid);
  const pc = createPublicClient({
    chain,
    transport: url ? http(url) : http(),
  });

  pcCache.set(cid, pc);
  return pc;
}

/* =========================
   Native USD price: TTL + inflight
   ✅ key = chainId (НЕ зависит от taker)
========================= */

const nativeUsdCache = new Map(); // chainId -> { ts, val }
const nativeUsdInflight = new Map(); // chainId -> Promise

async function fetchNativeUsdPer1(chainId, takerAddress) {
  const key = String(Number(chainId));
  const now = Date.now();

  const c = nativeUsdCache.get(key);
  if (c && now - c.ts < NATIVE_USD_TTL) return c.val;

  if (nativeUsdInflight.has(key)) return nativeUsdInflight.get(key);

  const p = (async () => {
    // 1 ETH/BNB/MATIC/etc in base units (18 decimals)
    const sellAmount = "1000000000000000000";

    const resp = await postAuthedJson("swapPrice", {
      chainId: Number(chainId),
      taker: takerAddress, // 0x требует taker формально
      sellToken: OX_NATIVE,
      sellAmount,
      slippageBps: 50,
    });

    const price =
      resp?.price || resp?.data?.price || resp?.result?.price || resp?.result;

    if (!resp?.ok || !price?.buyAmount) {
      nativeUsdCache.set(key, { ts: Date.now(), val: null });
      return null;
    }

    const stableDecimals = STABLE_DECIMALS_BY_CHAIN[chainId] ?? 6;
    const buyUsd = Number(formatUnits(BigInt(price.buyAmount), stableDecimals));
    const val = Number.isFinite(buyUsd) && buyUsd > 0 ? buyUsd : null;

    nativeUsdCache.set(key, { ts: Date.now(), val });
    return val;
  })();

  nativeUsdInflight.set(key, p);
  try {
    return await p;
  } finally {
    nativeUsdInflight.delete(key);
  }
}

/* =========================
   Scan-level dedupe + TTL
========================= */

const scanInflight = new Map(); // key -> Promise
const scanCache = new Map(); // key -> {ts, data}

function scanKeyOf({ address, walletChainId }) {
  return `${String(address || "").toLowerCase()}:${Number(walletChainId || 0)}`;
}

/* =========================
   Decimals fast-path
========================= */

function decimalsFast(chainId, token) {
  const sym = String(token?.symbol || "").toUpperCase();
  if (token?.isNative) return 18;

  if (sym === "USDT") return STABLE_DECIMALS_BY_CHAIN[chainId] ?? 6;
  if (sym === "USDC") return USDC_DECIMALS_BY_CHAIN[chainId] ?? 6;
  if (sym === "USDE") return 18;

  // если неизвестно — дефолт 18 (чтобы не дергать decimals() в скане)
  // Для "быстрого avail" нам важнее стабильные.
  return 18;
}

function isStableSymbol(sym) {
  const s = String(sym || "").toUpperCase();
  return s === "USDT" || s === "USDC" || s === "USDE";
}

/* =========================
   MAIN: scanMultiChainAvail
========================= */

/**
 * Возвращает массив:
 * [{ chainId, token, amountNum, amountStr, usdNum }]
 *
 * Логика:
 * - Stable ERC20: USD = amount
 * - Native: USD = amount * nativeUsdPer1(chain)
 * - Non-stable ERC20: USD=0 (не считаем в быстром скане)
 */
export async function scanMultiChainAvail({ address, walletChainId }) {
  const addr = String(address || "").trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) throw new Error("Bad address");

  const supported = Array.isArray(SUPPORTED_CHAINS) ? SUPPORTED_CHAINS : [];
  const chains = supported.filter((c) => TOKENS_BY_CHAIN?.[c]?.length);

  // scan-level TTL + inflight
  const skey = scanKeyOf({ address: addr, walletChainId });
  const now = Date.now();

  const cached = scanCache.get(skey);
  if (cached && now - cached.ts < SCAN_TTL_MS) return cached.data;

  if (scanInflight.has(skey)) return scanInflight.get(skey);

  const p = (async () => {
    const out = [];

    // nativeUsd prices prefetch per chain (dedup внутри fetchNativeUsdPer1)
    // taker нужен формально -> берем addr
    // делаем лениво: только если на чейне есть native token в списке
    const nativeUsdByChain = new Map();

    for (const cid of chains) {
      const list = TOKENS_BY_CHAIN[cid] || [];
      const hasNative = list.some((t) => t.isNative);
      if (!hasNative) continue;

      try {
        const v = await fetchNativeUsdPer1(cid, addr);
        nativeUsdByChain.set(cid, v);
      } catch (e) {
        // не падаем
        nativeUsdByChain.set(cid, null);
      }
    }

    // per chain scan
    for (const cid of chains) {
      const pc = makePublicClient(cid);
      if (!pc) continue;

      const list = TOKENS_BY_CHAIN[cid] || [];
      if (!list.length) continue;

      const nativeToken = list.find((t) => t.isNative);
      const erc20 = list.filter((t) => !t.isNative);

      // 1) native balance (1 RPC)
      if (nativeToken) {
        try {
          const raw = await pc.getBalance({ address: addr });
          const amountNum = Number(formatUnits(raw, 18));
          const px = nativeUsdByChain.get(cid) ?? null;

          const usdNum =
            Number.isFinite(amountNum) && amountNum > 0 && Number.isFinite(px) && px > 0
              ? amountNum * px
              : 0;

          if (usdNum >= USD_THRESHOLD) {
            out.push({
              chainId: cid,
              token: nativeToken,
              amountNum,
              amountStr: amountNum.toFixed(6),
              usdNum,
            });
          }
        } catch {
          // ignore
        }
      }

      // 2) ERC20 balances (rate-limit concurrency)
      const limit = cid === 1 ? ERC20_CONCURRENCY_MAINNET : ERC20_CONCURRENCY_DEFAULT;

      const results = await mapLimit(erc20, limit, async (t) => {
        // В "быстром avail" учитываем только stable
        const sym = String(t.symbol || "").toUpperCase();
        const isStable = isStableSymbol(sym);

        try {
          const raw = await pc.readContract({
            address: t.address,
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [addr],
          });

          const dec = decimalsFast(cid, t);
          const amountNum = Number(formatUnits(raw, dec));
          if (!Number.isFinite(amountNum) || amountNum <= 0) return null;

          const usdNum = isStable ? amountNum : 0;
          if (usdNum < USD_THRESHOLD) return null;

          return {
            chainId: cid,
            token: t,
            amountNum,
            amountStr: amountNum.toFixed(6),
            usdNum,
          };
        } catch (e) {
          // Если Infura даёт 429, лимит уже помогает. Ошибку глотаем.
          return null;
        }
      });

      for (const r of results) if (r) out.push(r);
    }

    // сортировка по usd убыв.
    out.sort((a, b) => Number(b.usdNum || 0) - Number(a.usdNum || 0));

    scanCache.set(skey, { ts: Date.now(), data: out });
    return out;
  })();

  scanInflight.set(skey, p);
  try {
    return await p;
  } finally {
    scanInflight.delete(skey);
  }
}
